import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { 
    Users, Search, Filter, Download, Trash2, AlertTriangle, Eye, 
    Plus, Minus, X, Check, Settings, Upload, ExternalLink, Calendar,
    Phone, Mail, MapPin, ChevronDown, ChevronUp, Edit2, MessageSquare,
    FileText, Flag, UserX, RefreshCw, MoreVertical, Copy, Link,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, QrCode, Archive, RotateCcw, GitMerge
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import SocialFaceDuplicates from '../components/SocialFaceDuplicates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SocialFacesAdminPage = () => {
    const { user, api } = useAuth();
    const isAdmin = user?.role === 'admin' || user?.role === 'chef';

    const [activeTab, setActiveTab] = useState('database');
    const [faces, setFaces] = useState([]);
    const [blacklistedFaces, setBlacklistedFaces] = useState([]);
    const [confirmations, setConfirmations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const ITEMS_PER_PAGE = 20;
    
    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [countryFilter, setCountryFilter] = useState('');
    const [genderFilter, setGenderFilter] = useState('');
    const [hairColorFilter, setHairColorFilter] = useState('');
    const [dietFilter, setDietFilter] = useState('');
    const [germanLevelFilter, setGermanLevelFilter] = useState('');
    const [tattoosFilter, setTattoosFilter] = useState('');
    const [piercingsFilter, setPiercingsFilter] = useState('');
    const [glassesFilter, setGlassesFilter] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [showArchived, setShowArchived] = useState(false);  // nur archivierte anzeigen
    const [duplicateCount, setDuplicateCount] = useState(null);
    const canMerge = isAdmin || user?.role === 'account_manager';
    const [sortBy, setSortBy] = useState('newest'); // newest, oldest, alphabetical, alphabetical_desc
    
    // Modals
    const [selectedFace, setSelectedFace] = useState(null);
    // Einwilligungen des gewaehlten Profils (Dokument, Fassung, Zeitpunkt, IP) - nur im Detail
    const [consentInfo, setConsentInfo] = useState(null);
    useEffect(() => {
        if (!selectedFace?.id) { setConsentInfo(null); return; }
        api.get(`/sf/consents/admin/${selectedFace.id}`).then(r => setConsentInfo(r.data)).catch(() => setConsentInfo(null));
    }, [selectedFace?.id]);  // eslint-disable-line react-hooks/exhaustive-deps
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showPointModal, setShowPointModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [selectedConfirmation, setSelectedConfirmation] = useState(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [editConfirmationMode, setEditConfirmationMode] = useState(false);
    const [editConfirmationForm, setEditConfirmationForm] = useState({});
    const [savingConfirmation, setSavingConfirmation] = useState(false);
    
    // Point reason
    const [pointReason, setPointReason] = useState('');
    
    // Settings
    const [settings, setSettings] = useState({ whatsapp_link: '', consent_pdf_url: null });

    // Helper function to ensure URL has protocol
    const ensureHttps = (url) => {
        if (!url) return url;
        const trimmed = url.trim();
        if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
            return trimmed;
        }
        return `https://${trimmed}`;
    };

    // CSV Import state
    const [showImportModal, setShowImportModal] = useState(false);
    const [importProgress, setImportProgress] = useState(null);
    const [isImporting, setIsImporting] = useState(false);

    // QR Code Modal
    const [showQrModal, setShowQrModal] = useState(false);

    // Helper function for status colors
    const getStatusColor = (status) => {
        switch (status) {
            case 'aktiv': return 'bg-green-500/20 text-green-400 border-green-500/30';
            case 'inaktiv': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
            case 'gesperrt': return 'bg-red-500/20 text-red-400 border-red-500/30';
            default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
        }
    };

    // Fetch data
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams();
            if (searchTerm) query.append('search', searchTerm);
            if (countryFilter) query.append('country', countryFilter);
            if (genderFilter) query.append('gender', genderFilter);
            if (hairColorFilter) query.append('hair_color', hairColorFilter);
            if (dietFilter) query.append('diet', dietFilter);
            if (germanLevelFilter) query.append('german_level', germanLevelFilter);
            if (tattoosFilter === 'yes') query.append('tattoos', 'true');
            if (tattoosFilter === 'no') query.append('tattoos', 'false');
            if (piercingsFilter === 'yes') query.append('piercings', 'true');
            if (piercingsFilter === 'no') query.append('piercings', 'false');
            if (glassesFilter === 'yes') query.append('glasses', 'true');
            if (glassesFilter === 'no') query.append('glasses', 'false');
            if (showArchived) query.append('only_archived', 'true');
            else query.append('is_blacklisted', 'false');
            query.append('page', currentPage.toString());
            query.append('limit', ITEMS_PER_PAGE.toString());
            query.append('sort_by', sortBy);
            
            const [facesRes, blacklistRes, confirmRes, statsRes, settingsRes] = await Promise.all([
                api.get(`/social-faces?${query.toString()}`),
                api.get('/social-faces?is_blacklisted=true&limit=100'),
                api.get('/shooting-confirmations'),
                api.get('/social-faces/admin/stats'),
                api.get('/social-faces/admin/settings')
            ]);
            
            setFaces(facesRes.data.items || []);
            setTotalCount(facesRes.data.total || 0);
            setTotalPages(Math.ceil((facesRes.data.total || 0) / ITEMS_PER_PAGE));
            setBlacklistedFaces(blacklistRes.data.items || []);
            setConfirmations(confirmRes.data.items || []);
            setStats(statsRes.data);
            setSettings(settingsRes.data);
        } catch (err) {
            console.error('Failed to fetch data:', err);
            toast.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    }, [api, searchTerm, countryFilter, genderFilter, hairColorFilter, dietFilter, germanLevelFilter, tattoosFilter, piercingsFilter, glassesFilter, currentPage, sortBy, showArchived]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, countryFilter, genderFilter, hairColorFilter, dietFilter, germanLevelFilter, tattoosFilter, piercingsFilter, glassesFilter, sortBy, showArchived]);

    // Reset all filters
    const resetFilters = () => {
        setSearchTerm('');
        setCountryFilter('');
        setGenderFilter('');
        setHairColorFilter('');
        setDietFilter('');
        setGermanLevelFilter('');
        setTattoosFilter('');
        setPiercingsFilter('');
        setGlassesFilter('');
    };

    // Count active filters
    const activeFilterCount = [
        countryFilter, genderFilter, hairColorFilter, dietFilter, 
        germanLevelFilter, tattoosFilter, piercingsFilter, glassesFilter
    ].filter(f => f).length;

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Add point
    const handleAddPoint = async () => {
        if (!selectedFace) return;
        
        try {
            await api.put(`/social-faces/${selectedFace.id}/add-point`, null, {
                params: { reason: pointReason || undefined }
            });
            toast.success('Punkt hinzugefügt');
            setShowPointModal(false);
            setPointReason('');
            fetchData();
        } catch (err) {
            toast.error('Fehler beim Hinzufügen des Punktes');
        }
    };

    // Update status
    const handleUpdateStatus = async (faceId, status) => {
        try {
            await api.put(`/social-faces/${faceId}/status?status=${status}`);
            toast.success('Status aktualisiert');
            fetchData();
        } catch (err) {
            toast.error('Fehler beim Aktualisieren des Status');
        }
    };

    // Delete face
    // Archivieren statt loeschen
    const handleArchive = async (faceId) => {
        const reason = window.prompt('Eintrag archivieren?\n\nEr verschwindet aus Listen und Zuweisungen, bleibt aber erhalten und kann wiederhergestellt werden.\n\nGrund (optional):', '');
        if (reason === null) return;
        try {
            await api.post(`/social-faces/${faceId}/archive`, { reason: reason || null });
            toast.success('Eintrag archiviert');
            setShowDetailModal(false);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Archivieren');
        }
    };

    // Selbstauskunft-Link (Anschrift, IBAN, ...) mailen; Link auch zum Kopieren anbieten
    const handleSendProfileLink = async (faceId) => {
        try {
            const res = await api.post(`/social-faces/${faceId}/send-profile-link`);
            const d = res.data || {};
            if (d.sent) toast.success(`Link an ${d.email} gesendet`);
            else toast.warning(`Keine Mail gesendet: ${d.reason || 'unbekannt'}. Link wurde in die Zwischenablage kopiert.`, { duration: 8000 });
            if (d.link && navigator.clipboard) { try { await navigator.clipboard.writeText(d.link); } catch (e) { /* ignore */ } }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Link konnte nicht erzeugt werden');
        }
    };

    const handleRestore = async (faceId) => {
        try {
            const res = await api.post(`/social-faces/${faceId}/restore`);
            toast.success(res.data?.hinweis || 'Eintrag wiederhergestellt');
            setShowDetailModal(false);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Wiederherstellen');
        }
    };

    // Endgueltig loeschen: nur Admin, nur archivierte Eintraege
    const handleDelete = async (faceId) => {
        if (!window.confirm('Endgültig löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
        
        try {
            await api.delete(`/social-faces/${faceId}`);
            toast.success('Eintrag endgültig gelöscht');
            setShowDetailModal(false);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
        }
    };

    // Remove from blacklist
    const handleRemoveFromBlacklist = async (faceId) => {
        try {
            await api.put(`/social-faces/${faceId}`, {
                is_blacklisted: false,
                points: 0
            });
            toast.success('Von Blacklist entfernt');
            fetchData();
        } catch (err) {
            toast.error('Fehler');
        }
    };

    // Save settings
    const handleSaveSettings = async () => {
        try {
            await api.put('/social-faces/admin/settings', settings);
            toast.success('Einstellungen gespeichert');
            setShowSettingsModal(false);
        } catch (err) {
            toast.error('Fehler beim Speichern');
        }
    };

    // Upload consent PDF
    const handleUploadPdf = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            const res = await api.post('/social-faces/admin/upload-consent-pdf', formData);
            setSettings(prev => ({ ...prev, consent_pdf_url: res.data.pdf_url }));
            toast.success('PDF hochgeladen');
        } catch (err) {
            toast.error('Fehler beim Hochladen');
        }
    };

    // Export PDF
    const handleExportPdf = async (confirmationId) => {
        try {
            const res = await api.get(`/shooting-confirmations/${confirmationId}/pdf`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `zusage_${confirmationId}.pdf`;
            a.click();
        } catch (err) {
            toast.error('Fehler beim Export');
        }
    };

    // Start editing a submitted shooting confirmation
    const handleStartEditConfirmation = () => {
        if (!selectedConfirmation) return;
        setEditConfirmationForm({
            client_name: selectedConfirmation.client_name || '',
            first_name: selectedConfirmation.first_name || '',
            last_name: selectedConfirmation.last_name || '',
            email: selectedConfirmation.email || '',
            phone: selectedConfirmation.phone || '',
            shooting_date: selectedConfirmation.shooting_date || '',
        });
        setEditConfirmationMode(true);
    };

    const handleCancelEditConfirmation = () => {
        setEditConfirmationMode(false);
        setEditConfirmationForm({});
    };

    const handleSaveConfirmationEdit = async () => {
        if (!selectedConfirmation) return;
        // Only send fields that actually changed
        const payload = {};
        Object.entries(editConfirmationForm).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== selectedConfirmation[k]) {
                payload[k] = v;
            }
        });
        if (Object.keys(payload).length === 0) {
            setEditConfirmationMode(false);
            return;
        }
        setSavingConfirmation(true);
        try {
            const res = await api.put(`/shooting-confirmations/${selectedConfirmation.id}`, payload);
            setSelectedConfirmation(res.data);
            setConfirmations(prev => prev.map(c => c.id === res.data.id ? res.data : c));
            setEditConfirmationMode(false);
            toast.success('Shooting-Zusage aktualisiert');
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Fehler beim Speichern';
            toast.error(msg);
        } finally {
            setSavingConfirmation(false);
        }
    };

    // CSV Import Handler
    const handleCsvImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        if (!file.name.endsWith('.csv')) {
            toast.error('Bitte eine CSV-Datei auswählen');
            return;
        }
        
        setIsImporting(true);
        setShowImportModal(true);
        setImportProgress({ status: 'uploading', message: 'CSV wird hochgeladen...', processed: 0, total: 0 });
        
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            // Start the import job
            const startRes = await api.post('/social-faces/admin/import-csv/start', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            const jobId = startRes.data.job_id;
            const total = startRes.data.total;
            
            setImportProgress({ 
                status: 'processing', 
                message: 'Import gestartet...', 
                processed: 0, 
                total: total,
                imported: 0,
                skipped: 0,
                withImage: 0,
                currentName: ''
            });
            
            // Poll for progress
            let pollErrorCount = 0;
            let pollStartTime = Date.now();
            const MAX_POLL_ERRORS = 5;
            const MAX_POLL_TIME = 3600000; // 60 minutes max for large imports
            
            const pollInterval = setInterval(async () => {
                // Safety timeout
                if (Date.now() - pollStartTime > MAX_POLL_TIME) {
                    clearInterval(pollInterval);
                    setIsImporting(false);
                    setImportProgress(prev => ({
                        ...prev,
                        status: 'error',
                        message: 'Import hat zu lange gedauert. Bitte Seite neu laden und prüfen.'
                    }));
                    return;
                }
                
                try {
                    const statusRes = await api.get(`/social-faces/admin/import-csv/status/${jobId}`);
                    const status = statusRes.data;
                    pollErrorCount = 0; // Reset on success
                    
                    setImportProgress({
                        status: status.status,
                        message: status.status === 'complete' 
                            ? `${status.imported} importiert (${status.with_image} mit Bild), ${status.skipped} übersprungen`
                            : `Verarbeite: ${status.current_name || '...'}`,
                        processed: status.processed,
                        total: status.total,
                        imported: status.imported,
                        skipped: status.skipped,
                        withImage: status.with_image,
                        withoutImage: status.without_image,
                        currentName: status.current_name,
                        errors: status.errors || []
                    });
                    
                    if (status.status === 'complete') {
                        clearInterval(pollInterval);
                        setIsImporting(false);
                        toast.success(`Import abgeschlossen: ${status.imported} importiert`);
                        fetchData();
                    }
                } catch (pollErr) {
                    console.error('Poll error:', pollErr);
                    pollErrorCount++;
                    
                    // After MAX_POLL_ERRORS consecutive errors, show error state but keep modal open
                    if (pollErrorCount >= MAX_POLL_ERRORS) {
                        clearInterval(pollInterval);
                        setIsImporting(false);
                        setImportProgress(prev => ({
                            ...prev,
                            status: 'error',
                            message: 'Verbindung zum Server verloren. Der Import läuft möglicherweise im Hintergrund weiter. Bitte Seite neu laden.'
                        }));
                    }
                }
            }, 2000); // Poll every 2 seconds
            
        } catch (err) {
            console.error('Import error:', err);
            setImportProgress({
                status: 'error',
                message: err.response?.data?.detail || 'Import fehlgeschlagen'
            });
            toast.error('Import fehlgeschlagen');
            setIsImporting(false);
        }
        
        // Reset file input
        e.target.value = '';
    };

    // Helper functions for display
    const getDietLabel = (diet) => {
        switch (diet) {
            case 'alles': return 'Alles';
            case 'vegetarisch': return 'Vegetarisch';
            case 'vegan': return 'Vegan';
            case 'kein_schwein': return 'Kein Schwein';
            default: return diet;
        }
    };

    const getGermanLabel = (level) => {
        switch (level) {
            case 'muttersprache': return 'Muttersprachler';
            case 'fortgeschritten': return 'Fortgeschritten';
            case 'mittelstufe': return 'Mittelstufe';
            case 'anfaenger': return 'Anfänger';
            case 'keine': return 'Keine';
            default: return level;
        }
    };

    // Face Card Component
    const FaceCard = ({ face, showBlacklistActions = false }) => (
        <Card className="hover:shadow-lg transition-all cursor-pointer group" onClick={() => { setSelectedFace(face); setShowDetailModal(true); }}>
            <CardContent className="p-3 sm:p-4">
                <div className="flex gap-3 sm:gap-4">
                    {/* Profile Image */}
                    <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                        {face.profile_image_url ? (
                            <img src={face.profile_image_url} alt={face.first_name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-lg sm:text-2xl font-bold text-muted-foreground">
                                {face.first_name?.[0]}{face.last_name?.[0]}
                            </div>
                        )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-start justify-between gap-1 sm:gap-2">
                            <div className="min-w-0">
                                <h3 className="font-semibold text-foreground flex items-center gap-1 sm:gap-2 text-sm sm:text-base truncate">
                                    <span className="truncate">{face.first_name} {face.last_name}</span>
                                    <span className="shrink-0">{face.country === 'DE' ? '🇩🇪' : '🇦🇹'}</span>
                                    <span className="text-[10px] sm:text-xs text-muted-foreground font-normal shrink-0">
                                        {face.gender === 'männlich' ? '♂️' : face.gender === 'weiblich' ? '♀️' : '⚧'}
                                    </span>
                                    {face.is_archived && (
                                        <Badge variant="outline" className="text-[10px] px-1.5 shrink-0" title={face.archived_reason || ''}>Archiviert</Badge>
                                    )}
                                    {face.is_minor_at_application && (
                                        <Badge variant="outline" className="text-[10px] px-1.5 shrink-0 bg-amber-500/10 border-amber-500/40 text-amber-500" title="Bei der Bewerbung unter 18 – Zustimmung der Erziehungsberechtigten hinterlegt">U18</Badge>
                                    )}
                                    {face.consent_revoked && Object.keys(face.consent_revoked).length > 0 && (
                                        <Badge variant="outline" className="text-[10px] px-1.5 shrink-0 bg-red-500/10 border-red-500/40 text-red-500" title={`Widerrufen: ${Object.keys(face.consent_revoked).join(', ')}`}>Widerruf</Badge>
                                    )}
                                </h3>
                                <p className="text-xs sm:text-sm text-muted-foreground truncate">{face.city} • {face.height}cm • {face.hair_color}</p>
                            </div>
                        </div>
                        
                        {/* Quick Info Badges */}
                        <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-1">
                            <Badge variant="outline" className="text-[10px] sm:text-xs px-1 sm:px-1.5">{getDietLabel(face.diet)}</Badge>
                            <Badge variant="outline" className="text-[10px] sm:text-xs px-1 sm:px-1.5">{getGermanLabel(face.german_level)}</Badge>
                            {face.tattoos && <Badge variant="outline" className="text-[10px] sm:text-xs px-1 sm:px-1.5 bg-purple-500/10 border-purple-500/30 text-purple-400">Tattoo</Badge>}
                            {face.piercings && <Badge variant="outline" className="text-[10px] sm:text-xs px-1 sm:px-1.5 bg-pink-500/10 border-pink-500/30 text-pink-400">Pierc.</Badge>}
                            {face.glasses && <Badge variant="outline" className="text-[10px] sm:text-xs px-1 sm:px-1.5 bg-blue-500/10 border-blue-500/30 text-blue-400">Brille</Badge>}
                        </div>
                        
                        {/* Contact - Hidden on mobile, shown on tap */}
                        <div className="mt-1.5 sm:mt-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-[10px] sm:text-xs text-muted-foreground">
                            <span className="flex items-center gap-1 truncate">
                                <Mail className="w-3 h-3 shrink-0" />
                                <span className="truncate">{face.email}</span>
                            </span>
                            {face.phone && (
                                <span className="flex items-center gap-1">
                                    <Phone className="w-3 h-3 shrink-0" />
                                    <span className="truncate">{face.phone}</span>
                                </span>
                            )}
                        </div>
                        
                        {/* Points */}
                        <div className="mt-1.5 sm:mt-2 flex items-center gap-1.5 sm:gap-2">
                            {[...Array(3)].map((_, i) => (
                                <div
                                    key={i}
                                    className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${
                                        i < face.points ? 'bg-red-500' : 'bg-muted'
                                    }`}
                                />
                            ))}
                            {face.points > 0 && (
                                <span className="text-[10px] sm:text-xs text-red-400 ml-0.5 sm:ml-1">{face.points}P</span>
                            )}
                        </div>
                    </div>
                    
                    {/* Actions (Admin only) */}
                    {isAdmin && (
                        <div className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0" onClick={e => e.stopPropagation()}>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9">
                                        <MoreVertical className="w-4 h-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => { setSelectedFace(face); setShowPointModal(true); }}>
                                        <AlertTriangle className="w-4 h-4 mr-2" /> Punkt vergeben
                                    </DropdownMenuItem>
                                    {showBlacklistActions && (
                                        <DropdownMenuItem onClick={() => handleRemoveFromBlacklist(face.id)}>
                                            <UserX className="w-4 h-4 mr-2" /> Von Blacklist entfernen
                                        </DropdownMenuItem>
                                    )}
                                    {canMerge && !face.is_archived && (
                                        <DropdownMenuItem onClick={() => handleSendProfileLink(face.id)}>
                                            <Mail className="w-4 h-4 mr-2" /> Stammdaten-Link senden
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    {face.is_archived ? (
                                        <>
                                            <DropdownMenuItem onClick={() => handleRestore(face.id)}>
                                                <RotateCcw className="w-4 h-4 mr-2" /> Wiederherstellen
                                            </DropdownMenuItem>
                                            <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(face.id)}>
                                                <Trash2 className="w-4 h-4 mr-2" /> Endgültig löschen
                                            </DropdownMenuItem>
                                        </>
                                    ) : (
                                        <DropdownMenuItem onClick={() => handleArchive(face.id)}>
                                            <Archive className="w-4 h-4 mr-2" /> Archivieren
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );

    // Confirmation Card Component
    const ConfirmationCard = ({ confirmation }) => (
        <Card className="hover:shadow-lg transition-all cursor-pointer" onClick={() => { setSelectedConfirmation(confirmation); setEditConfirmationMode(false); setEditConfirmationForm({}); setShowConfirmationModal(true); }}>
            <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                    <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-foreground text-sm sm:text-base truncate">
                            {confirmation.first_name} {confirmation.last_name}
                        </h3>
                        <p className="text-xs sm:text-sm text-muted-foreground truncate">Kunde: {confirmation.client_name}</p>
                        <div className="mt-1.5 sm:mt-2 flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3 shrink-0" />
                                <span className="truncate">{confirmation.shooting_date}</span>
                            </span>
                            <span className="flex items-center gap-1 hidden sm:flex">
                                <Mail className="w-3 h-3 shrink-0" />
                                <span className="truncate">{confirmation.email}</span>
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px] sm:text-xs">
                            Bestätigt
                        </Badge>
                        <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={(e) => { e.stopPropagation(); handleExportPdf(confirmation.id); }}>
                            <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    return (
        <div className="min-h-screen bg-background overflow-x-hidden" data-testid="social-faces-admin-page">
            <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8 overflow-x-hidden">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground">Social Faces</h1>
                    <p className="text-muted-foreground">Talentmanagement & Bewerbungen</p>
                </div>
                
                <div className="flex items-center gap-3">
                    <Button variant="outline" onClick={fetchData} disabled={loading}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Aktualisieren
                    </Button>
                    {isAdmin && (
                        <>
                            <Button variant="outline" onClick={() => setShowSettingsModal(true)}>
                                <Settings className="w-4 h-4 mr-2" />
                                Einstellungen
                            </Button>
                            <label className="cursor-pointer">
                                <input
                                    type="file"
                                    accept=".csv"
                                    className="hidden"
                                    onChange={handleCsvImport}
                                    disabled={isImporting}
                                />
                                <Button variant="outline" size="sm" asChild disabled={isImporting}>
                                    <span className="flex items-center gap-2 text-xs">
                                        <Upload className="w-3 h-3" />
                                        CSV
                                    </span>
                                </Button>
                            </label>
                        </>
                    )}
                </div>
            </div>

            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <Card>
                        <CardContent className="p-4 text-center">
                            <div className="text-2xl font-bold text-foreground">{stats.total}</div>
                            <div className="text-sm text-muted-foreground">Gesamt</div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <div className="text-2xl font-bold text-blue-500">{stats.new}</div>
                            <div className="text-sm text-muted-foreground">Neu</div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <div className="text-2xl font-bold text-red-500">{stats.blacklisted}</div>
                            <div className="text-sm text-muted-foreground">Blacklist</div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <div className="text-2xl font-bold text-purple-500">{stats.confirmations_total}</div>
                            <div className="text-sm text-muted-foreground">Zusagen</div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Social Face Zusage Link - Prominent Copy Buttons */}
            <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
                <CardContent className="p-4">
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                                <Link className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-sm">Social Face Zusage Link</h3>
                                <p className="text-xs text-muted-foreground">Öffentlicher Link für Shooting-Zusagen</p>
                            </div>
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => setShowQrModal(true)}
                                className="shrink-0"
                                data-testid="show-qr-code"
                                title="QR-Code anzeigen"
                            >
                                <QrCode className="w-5 h-5" />
                            </Button>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Button
                                onClick={() => {
                                    const baseUrl = window.location.origin;
                                    const fullUrl = `${baseUrl}/shooting-zusage/`;
                                    navigator.clipboard.writeText(fullUrl).then(() => {
                                        toast.success('Link kopiert!', {
                                            description: fullUrl
                                        });
                                    }).catch(() => {
                                        toast.error('Kopieren fehlgeschlagen');
                                    });
                                }}
                                className="gap-2 flex-1"
                                data-testid="copy-zusage-link"
                            >
                                <Copy className="w-4 h-4" />
                                Link kopieren
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    const baseUrl = window.location.origin;
                                    const zusageLink = `${baseUrl}/shooting-zusage/`;
                                    const fullMessage = `Hey 👋, 

danke für dein Interesse an unserem Social Media Shooting. Gerne bestätige ich dieses. Anbei nochmals die Hardfacts ⬇️. Bitte denke daran, dass ich noch eine finale Bestätigung von dir benötige. Bestätige bitte kurz über den Link, ob du dabei bist 👉 Link: ${zusageLink}

👀 Kunde: https://www.kunde.at/
😍 Personen: 4x
📆 Datum: Do., 02.10.2025
🕐 Zeit: 14:30-16:30
📌 Wo: Adresse 45, 1200 Wien
👚 Kleidung: Modern Classic
⚠️ Bezahlung: 15€/Stunde
📸 Auftragsbeschreibung: Foto & Video Produktion

📲 Kontaktperson vor Ort: Philipp - 0664 533 45 30

Wir freuen uns auf Dich! 😎 Falls etwas dazwischenkommen sollte, bitten wir Dich, uns frühzeitig zu kontaktieren. 

LG 🚀 Philipp | Guardians Member`;
                                    navigator.clipboard.writeText(fullMessage).then(() => {
                                        toast.success('Textnachricht kopiert!', {
                                            description: 'Komplette Vorlage in Zwischenablage'
                                        });
                                    }).catch(() => {
                                        toast.error('Kopieren fehlgeschlagen');
                                    });
                                }}
                                className="gap-2 flex-1"
                                data-testid="copy-full-message"
                            >
                                <MessageSquare className="w-4 h-4" />
                                Komplette Textnachricht kopieren
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 mt-4">
                <TabsList className="bg-muted/50 p-1 w-full grid grid-cols-4">
                    <TabsTrigger value="database" className="flex items-center justify-center gap-1 text-xs px-1.5 py-1.5">
                        <Users className="w-3.5 h-3.5 shrink-0" />
                        <span className="hidden sm:inline">Datenbank</span>
                        <span className="sm:hidden">DB</span>
                        <Badge variant="secondary" className="text-[10px] px-1 h-4 min-w-[1.25rem]">{faces.length}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="blacklist" className="flex items-center justify-center gap-1 text-xs px-1.5 py-1.5">
                        <UserX className="w-3.5 h-3.5 shrink-0" />
                        <span className="hidden sm:inline">Blacklist</span>
                        <span className="sm:hidden">BL</span>
                        <Badge variant="destructive" className="text-[10px] px-1 h-4 min-w-[1.25rem]">{blacklistedFaces.length}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="confirmations" className="flex items-center justify-center gap-1 text-xs px-1.5 py-1.5">
                        <FileText className="w-3.5 h-3.5 shrink-0" />
                        <span className="hidden sm:inline">Zusagen</span>
                        <span className="sm:hidden">ZS</span>
                        <Badge variant="secondary" className="text-[10px] px-1 h-4 min-w-[1.25rem]">{confirmations.length}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="duplicates" className="flex items-center justify-center gap-1 text-xs px-1.5 py-1.5" data-testid="tab-duplicates">
                        <GitMerge className="w-3.5 h-3.5 shrink-0" />
                        <span className="hidden sm:inline">Dubletten</span>
                        <span className="sm:hidden">DU</span>
                        {duplicateCount !== null && (
                            <Badge variant="secondary" className="text-[10px] px-1 h-4 min-w-[1.25rem]">{duplicateCount}</Badge>
                        )}
                    </TabsTrigger>
                </TabsList>

                {/* Database Tab */}
                <TabsContent value="database" className="space-y-4">
                    {/* Search and Filter Toggle */}
                    <div className="flex flex-wrap gap-3 items-center">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Name, Email, Stadt suchen..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <Button 
                            variant={showFilters ? "default" : "outline"} 
                            onClick={() => setShowFilters(!showFilters)}
                            className="relative"
                        >
                            <Filter className="w-4 h-4 mr-2" />
                            Filter
                            {activeFilterCount > 0 && (
                                <Badge variant="destructive" className="ml-2 px-1.5 py-0 text-xs">{activeFilterCount}</Badge>
                            )}
                        </Button>
                        <Button
                            variant={showArchived ? "default" : "outline"}
                            onClick={() => setShowArchived(!showArchived)}
                            data-testid="toggle-archived-faces"
                            title="Archivierte Einträge anzeigen und wiederherstellen"
                        >
                            <Archive className="w-4 h-4 mr-2" />
                            {showArchived ? 'Aktive anzeigen' : `Archivierte${stats?.archived ? ` (${stats.archived})` : ''}`}
                        </Button>
                        {/* Sort Dropdown */}
                        <Select value={sortBy} onValueChange={setSortBy}>
                            <SelectTrigger className="w-[140px]">
                                <SelectValue placeholder="Sortierung" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="newest">Neueste zuerst</SelectItem>
                                <SelectItem value="oldest">Älteste zuerst</SelectItem>
                                <SelectItem value="alphabetical">A-Z</SelectItem>
                                <SelectItem value="alphabetical_desc">Z-A</SelectItem>
                            </SelectContent>
                        </Select>
                        {activeFilterCount > 0 && (
                            <Button variant="ghost" size="sm" onClick={resetFilters}>
                                <X className="w-4 h-4 mr-1" /> Zurücksetzen
                            </Button>
                        )}
                    </div>
                    
                    {/* Extended Filters */}
                    <AnimatePresence>
                        {showFilters && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                            >
                                <Card>
                                    <CardContent className="p-4">
                                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                            {/* Country */}
                                            <div>
                                                <Label className="text-xs text-muted-foreground mb-1 block">Land</Label>
                                                <Select value={countryFilter || 'all'} onValueChange={(v) => setCountryFilter(v === 'all' ? '' : v)}>
                                                    <SelectTrigger className="h-9">
                                                        <SelectValue placeholder="Alle" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="all">Alle</SelectItem>
                                                        <SelectItem value="AT">🇦🇹 Österreich</SelectItem>
                                                        <SelectItem value="DE">🇩🇪 Deutschland</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            
                                            {/* Gender */}
                                            <div>
                                                <Label className="text-xs text-muted-foreground mb-1 block">Geschlecht</Label>
                                                <Select value={genderFilter || 'all'} onValueChange={(v) => setGenderFilter(v === 'all' ? '' : v)}>
                                                    <SelectTrigger className="h-9">
                                                        <SelectValue placeholder="Alle" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="all">Alle</SelectItem>
                                                        <SelectItem value="männlich">Männlich</SelectItem>
                                                        <SelectItem value="weiblich">Weiblich</SelectItem>
                                                        <SelectItem value="divers">Divers</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            
                                            {/* Hair Color */}
                                            <div>
                                                <Label className="text-xs text-muted-foreground mb-1 block">Haarfarbe</Label>
                                                <Select value={hairColorFilter || 'all'} onValueChange={(v) => setHairColorFilter(v === 'all' ? '' : v)}>
                                                    <SelectTrigger className="h-9">
                                                        <SelectValue placeholder="Alle" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="all">Alle</SelectItem>
                                                        <SelectItem value="schwarz">Schwarz</SelectItem>
                                                        <SelectItem value="braun">Braun</SelectItem>
                                                        <SelectItem value="blond">Blond</SelectItem>
                                                        <SelectItem value="rot">Rot</SelectItem>
                                                        <SelectItem value="grau">Grau</SelectItem>
                                                        <SelectItem value="weiß">Weiß</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            
                                            {/* Diet */}
                                            <div>
                                                <Label className="text-xs text-muted-foreground mb-1 block">Ernährung</Label>
                                                <Select value={dietFilter || 'all'} onValueChange={(v) => setDietFilter(v === 'all' ? '' : v)}>
                                                    <SelectTrigger className="h-9">
                                                        <SelectValue placeholder="Alle" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="all">Alle</SelectItem>
                                                        <SelectItem value="alles">Alles</SelectItem>
                                                        <SelectItem value="vegetarisch">Vegetarisch</SelectItem>
                                                        <SelectItem value="vegan">Vegan</SelectItem>
                                                        <SelectItem value="kein_schwein">Kein Schwein</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            
                                            {/* German Level */}
                                            <div>
                                                <Label className="text-xs text-muted-foreground mb-1 block">Deutsch</Label>
                                                <Select value={germanLevelFilter || 'all'} onValueChange={(v) => setGermanLevelFilter(v === 'all' ? '' : v)}>
                                                    <SelectTrigger className="h-9">
                                                        <SelectValue placeholder="Alle" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="all">Alle</SelectItem>
                                                        <SelectItem value="muttersprache">Muttersprachler</SelectItem>
                                                        <SelectItem value="fortgeschritten">Fortgeschritten</SelectItem>
                                                        <SelectItem value="mittelstufe">Mittelstufe</SelectItem>
                                                        <SelectItem value="anfaenger">Anfänger</SelectItem>
                                                        <SelectItem value="keine">Keine</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            
                                            {/* Tattoos */}
                                            <div>
                                                <Label className="text-xs text-muted-foreground mb-1 block">Tattoos</Label>
                                                <Select value={tattoosFilter || 'all'} onValueChange={(v) => setTattoosFilter(v === 'all' ? '' : v)}>
                                                    <SelectTrigger className="h-9">
                                                        <SelectValue placeholder="Alle" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="all">Alle</SelectItem>
                                                        <SelectItem value="yes">Mit Tattoos</SelectItem>
                                                        <SelectItem value="no">Ohne Tattoos</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            
                                            {/* Piercings */}
                                            <div>
                                                <Label className="text-xs text-muted-foreground mb-1 block">Piercings</Label>
                                                <Select value={piercingsFilter || 'all'} onValueChange={(v) => setPiercingsFilter(v === 'all' ? '' : v)}>
                                                    <SelectTrigger className="h-9">
                                                        <SelectValue placeholder="Alle" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="all">Alle</SelectItem>
                                                        <SelectItem value="yes">Mit Piercings</SelectItem>
                                                        <SelectItem value="no">Ohne Piercings</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            
                                            {/* Glasses */}
                                            <div>
                                                <Label className="text-xs text-muted-foreground mb-1 block">Brille</Label>
                                                <Select value={glassesFilter || 'all'} onValueChange={(v) => setGlassesFilter(v === 'all' ? '' : v)}>
                                                    <SelectTrigger className="h-9">
                                                        <SelectValue placeholder="Alle" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="all">Alle</SelectItem>
                                                        <SelectItem value="yes">Mit Brille</SelectItem>
                                                        <SelectItem value="no">Ohne Brille</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Face List */}
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : faces.length === 0 ? (
                        <Card>
                            <CardContent className="p-12 text-center">
                                <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                                <h3 className="text-lg font-medium text-foreground mb-2">Keine Einträge</h3>
                                <p className="text-muted-foreground">Es wurden noch keine Bewerbungen eingereicht.</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid gap-3">
                                {faces.map(face => (
                                    <FaceCard key={face.id} face={face} />
                                ))}
                            </div>
                            
                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-between border-t pt-4">
                                    <div className="text-sm text-muted-foreground">
                                        Seite {currentPage} von {totalPages} ({totalCount} Einträge)
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-8 w-8"
                                            onClick={() => setCurrentPage(1)}
                                            disabled={currentPage === 1}
                                        >
                                            <ChevronsLeft className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-8 w-8"
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        
                                        {/* Page numbers */}
                                        <div className="flex items-center gap-1 mx-2">
                                            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }
                                                return (
                                                    <Button
                                                        key={pageNum}
                                                        variant={currentPage === pageNum ? "default" : "outline"}
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        onClick={() => setCurrentPage(pageNum)}
                                                    >
                                                        {pageNum}
                                                    </Button>
                                                );
                                            })}
                                        </div>
                                        
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-8 w-8"
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-8 w-8"
                                            onClick={() => setCurrentPage(totalPages)}
                                            disabled={currentPage === totalPages}
                                        >
                                            <ChevronsRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </TabsContent>

                {/* Blacklist Tab */}
                <TabsContent value="blacklist" className="space-y-4">
                    {blacklistedFaces.length === 0 ? (
                        <Card>
                            <CardContent className="p-12 text-center">
                                <UserX className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                                <h3 className="text-lg font-medium text-foreground mb-2">Keine Einträge</h3>
                                <p className="text-muted-foreground">Die Blacklist ist leer.</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid gap-3">
                            {blacklistedFaces.map(face => (
                                <FaceCard key={face.id} face={face} showBlacklistActions />
                            ))}
                        </div>
                    )}
                </TabsContent>

                {/* Dubletten Tab */}
                <TabsContent value="duplicates" className="space-y-4">
                    {activeTab === 'duplicates' && (
                        <SocialFaceDuplicates api={api} canMerge={canMerge} onChanged={fetchData} onCount={setDuplicateCount} />
                    )}
                </TabsContent>

                {/* Confirmations Tab */}
                <TabsContent value="confirmations" className="space-y-4">
                    {confirmations.length === 0 ? (
                        <Card>
                            <CardContent className="p-12 text-center">
                                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                                <h3 className="text-lg font-medium text-foreground mb-2">Keine Zusagen</h3>
                                <p className="text-muted-foreground">Es wurden noch keine Shooting-Zusagen eingereicht.</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid gap-3">
                            {confirmations.map(conf => (
                                <ConfirmationCard key={conf.id} confirmation={conf} />
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Detail Modal */}
            <Dialog open={showDetailModal} onOpenChange={(open) => { setShowDetailModal(open); if (!open) setSelectedFace(null); }}>
                <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                    {selectedFace && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-3">
                                    <span>{selectedFace.first_name} {selectedFace.last_name}</span>
                                    <span>{selectedFace.country === 'DE' ? '🇩🇪' : '🇦🇹'}</span>
                                    <Badge className={getStatusColor(selectedFace.status)}>{selectedFace.status}</Badge>
                                </DialogTitle>
                            </DialogHeader>
                            
                            <div className="grid md:grid-cols-2 gap-6">
                                {/* Image */}
                                <div>
                                    {selectedFace.profile_image_url ? (
                                        <img src={selectedFace.profile_image_url} alt="" className="w-full rounded-xl" />
                                    ) : (
                                        <div className="w-full aspect-square bg-muted rounded-xl flex items-center justify-center text-4xl font-bold text-muted-foreground">
                                            {selectedFace.first_name?.[0]}{selectedFace.last_name?.[0]}
                                        </div>
                                    )}
                                    
                                    {/* Points */}
                                    <div className="mt-4 p-4 bg-muted/50 rounded-xl">
                                        <div className="text-sm text-muted-foreground mb-2">Punkte-System</div>
                                        <div className="flex items-center gap-3">
                                            {[...Array(3)].map((_, i) => (
                                                <div
                                                    key={i}
                                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                                        i < (selectedFace.points || 0) 
                                                            ? 'bg-red-500 text-white' 
                                                            : 'bg-muted text-muted-foreground'
                                                    }`}
                                                >
                                                    {i + 1}
                                                </div>
                                            ))}
                                            {(selectedFace.points || 0) >= 3 && (
                                                <Badge variant="destructive">Gesperrt</Badge>
                                            )}
                                        </div>
                                        {selectedFace.point_history && selectedFace.point_history.length > 0 && (
                                            <div className="mt-3 space-y-1 text-xs">
                                                {selectedFace.point_history.map((entry, i) => (
                                                    <div key={i} className="text-muted-foreground">
                                                        • {entry.date?.slice(0, 10)}: {entry.reason || 'Kein Grund angegeben'}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Details */}
                                <div className="space-y-4">
                                    {consentInfo && (
                                        <div className="p-3 bg-muted/50 rounded-lg space-y-2" data-testid="face-consents">
                                            <div className="text-xs text-muted-foreground flex items-center justify-between">
                                                <span>Einwilligungen</span>
                                                <span className={consentInfo.complete ? 'text-green-500' : 'text-amber-500'}>{consentInfo.complete ? 'vollständig' : `${consentInfo.missing.length} offen`}</span>
                                            </div>
                                            {consentInfo.documents.map(d => (
                                                <div key={d.key} className="text-xs flex items-start justify-between gap-2">
                                                    <a href={d.url} target="_blank" rel="noopener noreferrer" className="underline truncate">{d.title}</a>
                                                    <span className={`shrink-0 ${d.revoked ? 'text-red-500' : d.accepted ? 'text-green-500' : 'text-amber-500'}`}>
                                                        {d.revoked ? `widerrufen ${d.last?.accepted_at?.slice(0, 10)}` : d.accepted ? `Fassung ${d.last?.version} · ${d.last?.accepted_at?.slice(0, 10)}` : `fehlt (aktuell Fassung ${d.version})`}
                                                    </span>
                                                </div>
                                            ))}
                                            {consentInfo.is_minor_at_application && consentInfo.guardian_consent && (
                                                <div className="text-xs text-amber-500 pt-1 border-t border-border/50">
                                                    U18 bei Bewerbung ({consentInfo.guardian_consent.age_at_application} J.) · Erziehungsberechtigte: {consentInfo.guardian_consent.name}, {consentInfo.guardian_consent.contact}
                                                    {consentInfo.guardian_consent.document_url && <> · <a href={consentInfo.guardian_consent.document_url} target="_blank" rel="noopener noreferrer" className="underline">Erklärung</a></>}
                                                </div>
                                            )}
                                            {consentInfo.history?.length > 0 && (
                                                <details className="text-[11px] text-muted-foreground">
                                                    <summary className="cursor-pointer">Protokoll ({consentInfo.history.length})</summary>
                                                    <ul className="mt-1 space-y-0.5">
                                                        {consentInfo.history.map((h, i) => (
                                                            <li key={i}>{h.accepted_at?.slice(0, 16).replace('T', ' ')} · {h.action === 'accept' ? 'Zustimmung' : 'Widerruf'} · {h.doc_key} v{h.version} · {h.source} · IP {h.ip}</li>
                                                        ))}
                                                    </ul>
                                                </details>
                                            )}
                                        </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="p-3 bg-muted/50 rounded-lg">
                                            <div className="text-xs text-muted-foreground">Geschlecht</div>
                                            <div className="font-medium">{selectedFace.gender}</div>
                                        </div>
                                        <div className="p-3 bg-muted/50 rounded-lg">
                                            <div className="text-xs text-muted-foreground">Geburtstag</div>
                                            <div className="font-medium">{selectedFace.birthday}</div>
                                        </div>
                                        <div className="p-3 bg-muted/50 rounded-lg">
                                            <div className="text-xs text-muted-foreground">Ort</div>
                                            <div className="font-medium">{selectedFace.postal_code} {selectedFace.city}</div>
                                        </div>
                                        <div className="p-3 bg-muted/50 rounded-lg">
                                            <div className="text-xs text-muted-foreground">Nationalität</div>
                                            <div className="font-medium">{selectedFace.nationality}</div>
                                        </div>
                                        <div className="p-3 bg-muted/50 rounded-lg">
                                            <div className="text-xs text-muted-foreground">Größe</div>
                                            <div className="font-medium">{selectedFace.height} cm</div>
                                        </div>
                                        <div className="p-3 bg-muted/50 rounded-lg">
                                            <div className="text-xs text-muted-foreground">Haarfarbe</div>
                                            <div className="font-medium">{selectedFace.hair_color}</div>
                                        </div>
                                        <div className="p-3 bg-muted/50 rounded-lg">
                                            <div className="text-xs text-muted-foreground">Deutschkenntnisse</div>
                                            <div className="font-medium">{selectedFace.german_level}</div>
                                        </div>
                                        <div className="p-3 bg-muted/50 rounded-lg">
                                            <div className="text-xs text-muted-foreground">Ernährung</div>
                                            <div className="font-medium">{selectedFace.diet}</div>
                                        </div>
                                    </div>
                                    
                                    {/* Contact */}
                                    <div className="space-y-2">
                                        {selectedFace.email && (
                                            <a href={`mailto:${selectedFace.email}`} className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                                                <Mail className="w-4 h-4 text-primary" />
                                                <span>{selectedFace.email}</span>
                                            </a>
                                        )}
                                        {selectedFace.phone && (
                                            <a href={`tel:${selectedFace.phone}`} className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                                                <Phone className="w-4 h-4 text-primary" />
                                                <span>{selectedFace.phone}</span>
                                            </a>
                                        )}
                                    </div>
                                    
                                    {/* Checkboxes */}
                                    <div className="flex flex-wrap gap-2">
                                        {selectedFace.glasses && <Badge variant="outline">Brille</Badge>}
                                        {selectedFace.piercings && <Badge variant="outline">Piercings</Badge>}
                                        {selectedFace.tattoos && <Badge variant="outline">Tattoos</Badge>}
                                    </div>
                                    
                                    {/* Allergies */}
                                    {selectedFace.allergies && selectedFace.allergies_details && (
                                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                                            <div className="text-xs text-red-400 mb-1">Allergien</div>
                                            <div className="text-sm">{selectedFace.allergies_details}</div>
                                        </div>
                                    )}
                                    
                                    {/* Other info */}
                                    {selectedFace.other_info && selectedFace.other_info_details && (
                                        <div className="p-3 bg-muted/50 rounded-lg">
                                            <div className="text-xs text-muted-foreground mb-1">Zusätzliche Infos</div>
                                            <div className="text-sm">{selectedFace.other_info_details}</div>
                                        </div>
                                    )}
                                    
                                    {/* Social Links */}
                                    {selectedFace.social_links && Array.isArray(selectedFace.social_links) && selectedFace.social_links.length > 0 && (
                                        <div className="space-y-2">
                                            <div className="text-xs text-muted-foreground">Social Media</div>
                                            {selectedFace.social_links.map((link, i) => (
                                                <a key={i} href={ensureHttps(link)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline">
                                                    <ExternalLink className="w-3 h-3" />
                                                    {link}
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* Instagram/TikTok */}
                                    {(selectedFace.instagram || selectedFace.tiktok) && (
                                        <div className="flex flex-wrap gap-2">
                                            {selectedFace.instagram && (
                                                <a 
                                                    href={`https://instagram.com/${String(selectedFace.instagram).replace('@', '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-sm hover:from-purple-500/30 hover:to-pink-500/30 transition-colors"
                                                >
                                                    <span>📸</span>
                                                    @{String(selectedFace.instagram).replace('@', '')}
                                                </a>
                                            )}
                                            {selectedFace.tiktok && (
                                                <a 
                                                    href={`https://tiktok.com/@${String(selectedFace.tiktok).replace('@', '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-black/10 dark:bg-white/10 text-sm hover:bg-black/20 dark:hover:bg-white/20 transition-colors"
                                                >
                                                    <span>🎵</span>
                                                    @{String(selectedFace.tiktok).replace('@', '')}
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Admin Actions */}
                            {isAdmin && (
                                <DialogFooter className="mt-6 flex-wrap gap-2">
                                    <Button variant="outline" onClick={() => { setShowDetailModal(false); setShowPointModal(true); }}>
                                        <AlertTriangle className="w-4 h-4 mr-2" />
                                        Punkt vergeben
                                    </Button>
                                    {selectedFace.is_archived ? (
                                        <>
                                            <Button variant="outline" onClick={() => handleRestore(selectedFace.id)}>
                                                <RotateCcw className="w-4 h-4 mr-2" />
                                                Wiederherstellen
                                            </Button>
                                            <Button variant="destructive" onClick={() => handleDelete(selectedFace.id)}>
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Endgültig löschen
                                            </Button>
                                        </>
                                    ) : (
                                        <Button variant="outline" onClick={() => handleArchive(selectedFace.id)}>
                                            <Archive className="w-4 h-4 mr-2" />
                                            Archivieren
                                        </Button>
                                    )}
                                </DialogFooter>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Point Modal */}
            <Dialog open={showPointModal} onOpenChange={setShowPointModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-yellow-500" />
                            Punkt vergeben
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedFace && (
                        <div className="space-y-4">
                            <p className="text-muted-foreground">
                                Du vergibst einen Punkt an <strong>{selectedFace.first_name} {selectedFace.last_name}</strong>.
                            </p>
                            
                            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                                <span className="text-sm text-muted-foreground">Aktuell:</span>
                                {[...Array(3)].map((_, i) => (
                                    <div
                                        key={i}
                                        className={`w-6 h-6 rounded-full ${
                                            i < selectedFace.points ? 'bg-red-500' : 'bg-muted'
                                        }`}
                                    />
                                ))}
                                <span className="text-sm text-muted-foreground">→</span>
                                {[...Array(3)].map((_, i) => (
                                    <div
                                        key={i}
                                        className={`w-6 h-6 rounded-full ${
                                            i < selectedFace.points + 1 ? 'bg-red-500' : 'bg-muted'
                                        }`}
                                    />
                                ))}
                            </div>
                            
                            {selectedFace.points >= 2 && (
                                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                                    ⚠️ Bei 3 Punkten wird diese Person automatisch auf die Blacklist gesetzt!
                                </div>
                            )}
                            
                            <div>
                                <Label>Grund (optional)</Label>
                                <Textarea
                                    value={pointReason}
                                    onChange={(e) => setPointReason(e.target.value)}
                                    placeholder="Warum wird dieser Punkt vergeben?"
                                    className="mt-1"
                                />
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPointModal(false)}>Abbrechen</Button>
                        <Button variant="destructive" onClick={handleAddPoint}>
                            Punkt vergeben
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Settings Modal */}
            <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Social Faces Einstellungen</DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        <div>
                            <Label>WhatsApp Gruppen-Link</Label>
                            <Input
                                value={settings.whatsapp_link}
                                onChange={(e) => setSettings(prev => ({ ...prev, whatsapp_link: e.target.value }))}
                                placeholder="https://chat.whatsapp.com/..."
                                className="mt-1"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Bewerber werden nach dem Absenden zu diesem Link weitergeleitet.
                            </p>
                        </div>
                        
                        <div>
                            <Label>Einverständniserklärung PDF</Label>
                            <div className="mt-1 space-y-2">
                                {settings.consent_pdf_url && (
                                    <a href={settings.consent_pdf_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline">
                                        <FileText className="w-4 h-4" />
                                        Aktuelles PDF anzeigen
                                    </a>
                                )}
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="file"
                                        accept=".pdf"
                                        onChange={handleUploadPdf}
                                        className="flex-1"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSettingsModal(false)}>Abbrechen</Button>
                        <Button onClick={handleSaveSettings}>Speichern</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirmation Detail Modal */}
            <Dialog open={showConfirmationModal} onOpenChange={(open) => {
                setShowConfirmationModal(open);
                if (!open) { setEditConfirmationMode(false); setEditConfirmationForm({}); }
            }}>
                <DialogContent>
                    {selectedConfirmation && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    Shooting-Zusage
                                    {editConfirmationMode && (
                                        <Badge variant="outline" className="text-xs">Bearbeitungs-Modus</Badge>
                                    )}
                                </DialogTitle>
                            </DialogHeader>
                            
                            <div className="space-y-4">
                                {editConfirmationMode ? (
                                    <div className="space-y-3" data-testid="edit-confirmation-form">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-1">
                                                <Label className="text-xs">Vorname</Label>
                                                <Input
                                                    value={editConfirmationForm.first_name || ''}
                                                    onChange={(e) => setEditConfirmationForm(f => ({ ...f, first_name: e.target.value }))}
                                                    data-testid="edit-conf-first-name"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs">Nachname</Label>
                                                <Input
                                                    value={editConfirmationForm.last_name || ''}
                                                    onChange={(e) => setEditConfirmationForm(f => ({ ...f, last_name: e.target.value }))}
                                                    data-testid="edit-conf-last-name"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Kunde</Label>
                                            <Input
                                                value={editConfirmationForm.client_name || ''}
                                                onChange={(e) => setEditConfirmationForm(f => ({ ...f, client_name: e.target.value }))}
                                                placeholder="Kundenname richtigstellen..."
                                                data-testid="edit-conf-client-name"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Shooting-Datum</Label>
                                            <Input
                                                type="date"
                                                value={editConfirmationForm.shooting_date || ''}
                                                onChange={(e) => setEditConfirmationForm(f => ({ ...f, shooting_date: e.target.value }))}
                                                data-testid="edit-conf-shooting-date"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">E-Mail</Label>
                                            <Input
                                                type="email"
                                                value={editConfirmationForm.email || ''}
                                                onChange={(e) => setEditConfirmationForm(f => ({ ...f, email: e.target.value }))}
                                                data-testid="edit-conf-email"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Telefon</Label>
                                            <Input
                                                value={editConfirmationForm.phone || ''}
                                                onChange={(e) => setEditConfirmationForm(f => ({ ...f, phone: e.target.value }))}
                                                data-testid="edit-conf-phone"
                                            />
                                        </div>
                                        <p className="text-xs text-muted-foreground italic">
                                            Hinweis: Unterschrift und Einverständnis bleiben aus Nachweisgründen unverändert.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="p-3 bg-muted/50 rounded-lg">
                                            <div className="text-xs text-muted-foreground">Name</div>
                                            <div className="font-medium">{selectedConfirmation.first_name} {selectedConfirmation.last_name}</div>
                                        </div>
                                        <div className="p-3 bg-muted/50 rounded-lg">
                                            <div className="text-xs text-muted-foreground">Kunde</div>
                                            <div className="font-medium">{selectedConfirmation.client_name}</div>
                                        </div>
                                        <div className="p-3 bg-muted/50 rounded-lg">
                                            <div className="text-xs text-muted-foreground">Shooting-Datum</div>
                                            <div className="font-medium">{selectedConfirmation.shooting_date}</div>
                                        </div>
                                        <div className="p-3 bg-muted/50 rounded-lg">
                                            <div className="text-xs text-muted-foreground">Eingereicht am</div>
                                            <div className="font-medium">{selectedConfirmation.created_at?.slice(0, 10)}</div>
                                        </div>
                                    </div>
                                )}
                                
                                {!editConfirmationMode && (
                                    <div className="space-y-2">
                                        <a href={`mailto:${selectedConfirmation.email}`} className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                                            <Mail className="w-4 h-4 text-primary" />
                                            <span>{selectedConfirmation.email}</span>
                                        </a>
                                        <a href={`tel:${selectedConfirmation.phone}`} className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                                            <Phone className="w-4 h-4 text-primary" />
                                            <span>{selectedConfirmation.phone}</span>
                                        </a>
                                    </div>
                                )}
                                
                                {!editConfirmationMode && selectedConfirmation.signature_url && (
                                    <div>
                                        <div className="text-xs text-muted-foreground mb-2">Unterschrift</div>
                                        <img src={selectedConfirmation.signature_url} alt="Unterschrift" className="border rounded-lg p-2 bg-white max-h-32" />
                                    </div>
                                )}
                                {!editConfirmationMode && selectedConfirmation.updated_by_name && (
                                    <p className="text-[10px] text-muted-foreground italic">
                                        Zuletzt bearbeitet von {selectedConfirmation.updated_by_name}
                                        {selectedConfirmation.updated_at && ` am ${selectedConfirmation.updated_at.slice(0, 10)}`}
                                    </p>
                                )}
                            </div>
                            
                            <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
                                {editConfirmationMode ? (
                                    <>
                                        <Button variant="outline" onClick={handleCancelEditConfirmation} disabled={savingConfirmation} data-testid="edit-conf-cancel">
                                            Abbrechen
                                        </Button>
                                        <Button onClick={handleSaveConfirmationEdit} disabled={savingConfirmation} data-testid="edit-conf-save">
                                            <Check className="w-4 h-4 mr-2" />
                                            {savingConfirmation ? 'Speichert…' : 'Speichern'}
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button variant="outline" onClick={() => handleExportPdf(selectedConfirmation.id)}>
                                            <Download className="w-4 h-4 mr-2" />
                                            Als PDF exportieren
                                        </Button>
                                        <Button onClick={handleStartEditConfirmation} data-testid="edit-conf-start">
                                            <Edit2 className="w-4 h-4 mr-2" />
                                            Bearbeiten
                                        </Button>
                                    </>
                                )}
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* CSV Import Progress Modal */}
            <Dialog open={showImportModal} onOpenChange={(open) => {
                // Only allow closing if not importing
                if (!open && !isImporting) {
                    setShowImportModal(false);
                    setImportProgress(null);
                }
            }}>
                <DialogContent className="max-w-md" onPointerDownOutside={(e) => {
                    // Prevent closing by clicking outside during import
                    if (isImporting) e.preventDefault();
                }} onEscapeKeyDown={(e) => {
                    // Prevent closing with Escape during import
                    if (isImporting) e.preventDefault();
                }}>
                    <DialogHeader>
                        <DialogTitle>CSV Import</DialogTitle>
                    </DialogHeader>
                    
                    {importProgress && (
                        <div className="space-y-4">
                            {importProgress.status === 'uploading' && (
                                <div className="flex items-center gap-3">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                                    <span>{importProgress.message}</span>
                                </div>
                            )}
                            
                            {(importProgress.status === 'processing' || importProgress.status === 'running') && (
                                <div className="space-y-4">
                                    {/* Progress counter */}
                                    <div className="text-center">
                                        <div className="text-3xl font-bold text-primary">
                                            {importProgress.processed || 0} / {importProgress.total || 0}
                                        </div>
                                        <p className="text-sm text-muted-foreground">Einträge verarbeitet</p>
                                    </div>
                                    
                                    {/* Progress bar */}
                                    <div className="w-full bg-muted rounded-full h-3">
                                        <div 
                                            className="bg-primary h-3 rounded-full transition-all duration-300"
                                            style={{ width: `${importProgress.total ? (importProgress.processed / importProgress.total) * 100 : 0}%` }}
                                        />
                                    </div>
                                    
                                    {/* Current item */}
                                    <div className="flex items-center gap-2 text-sm">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                                        <span className="text-muted-foreground truncate">
                                            {importProgress.currentName || 'Verarbeite...'}
                                        </span>
                                    </div>
                                    
                                    {/* Live stats */}
                                    <div className="grid grid-cols-3 gap-2 text-xs">
                                        <div className="p-2 rounded bg-green-500/10 text-center">
                                            <div className="font-bold text-green-500">{importProgress.imported || 0}</div>
                                            <div className="text-muted-foreground">Importiert</div>
                                        </div>
                                        <div className="p-2 rounded bg-blue-500/10 text-center">
                                            <div className="font-bold text-blue-500">{importProgress.withImage || 0}</div>
                                            <div className="text-muted-foreground">Mit Bild</div>
                                        </div>
                                        <div className="p-2 rounded bg-yellow-500/10 text-center">
                                            <div className="font-bold text-yellow-500">{importProgress.skipped || 0}</div>
                                            <div className="text-muted-foreground">Übersprungen</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {importProgress.status === 'complete' && (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 text-green-500">
                                        <Check className="w-5 h-5" />
                                        <span className="font-medium">Import abgeschlossen!</span>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div className="p-3 rounded-lg bg-muted/50">
                                            <div className="text-2xl font-bold text-foreground">{importProgress.total}</div>
                                            <div className="text-muted-foreground">Gesamt</div>
                                        </div>
                                        <div className="p-3 rounded-lg bg-green-500/10">
                                            <div className="text-2xl font-bold text-green-500">{importProgress.imported}</div>
                                            <div className="text-muted-foreground">Importiert</div>
                                        </div>
                                        <div className="p-3 rounded-lg bg-blue-500/10">
                                            <div className="text-2xl font-bold text-blue-500">{importProgress.withImage}</div>
                                            <div className="text-muted-foreground">Mit Bild</div>
                                        </div>
                                        <div className="p-3 rounded-lg bg-yellow-500/10">
                                            <div className="text-2xl font-bold text-yellow-500">{importProgress.skipped}</div>
                                            <div className="text-muted-foreground">Übersprungen</div>
                                        </div>
                                    </div>
                                    
                                    {importProgress.errors && importProgress.errors.length > 0 && (
                                        <div className="p-3 rounded-lg bg-red-500/10 text-sm">
                                            <div className="font-medium text-red-500 mb-2">Fehler:</div>
                                            <ul className="list-disc list-inside text-muted-foreground text-xs space-y-1">
                                                {importProgress.errors.map((err, i) => (
                                                    <li key={i}>{err}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    
                                    <Button onClick={() => { setShowImportModal(false); setImportProgress(null); }} className="w-full">
                                        Schließen
                                    </Button>
                                </div>
                            )}
                            
                            {importProgress.status === 'error' && (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 text-red-500">
                                        <X className="w-5 h-5" />
                                        <span className="font-medium">Import fehlgeschlagen</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground">{importProgress.message}</p>
                                    <Button variant="outline" onClick={() => { setShowImportModal(false); setImportProgress(null); }} className="w-full">
                                        Schließen
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* QR Code Modal */}
            <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <QrCode className="w-5 h-5 text-primary" />
                            QR-Code für Shooting-Zusage
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col items-center gap-4 py-4">
                        <div className="bg-white p-4 rounded-xl shadow-lg">
                            <QRCodeSVG 
                                value={`${window.location.origin}/shooting-zusage/`}
                                size={200}
                                level="H"
                                includeMargin={true}
                            />
                        </div>
                        <p className="text-sm text-muted-foreground text-center">
                            Scanne diesen QR-Code, um das Zusage-Formular zu öffnen
                        </p>
                        <div className="flex gap-2 w-full">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                    // Download QR code as PNG
                                    const svg = document.querySelector('.qr-download-target');
                                    if (svg) {
                                        const canvas = document.createElement('canvas');
                                        const ctx = canvas.getContext('2d');
                                        const img = new Image();
                                        const svgData = new XMLSerializer().serializeToString(svg);
                                        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
                                        const url = URL.createObjectURL(svgBlob);
                                        
                                        img.onload = () => {
                                            canvas.width = 300;
                                            canvas.height = 300;
                                            ctx.fillStyle = 'white';
                                            ctx.fillRect(0, 0, 300, 300);
                                            ctx.drawImage(img, 50, 50, 200, 200);
                                            
                                            const pngUrl = canvas.toDataURL('image/png');
                                            const downloadLink = document.createElement('a');
                                            downloadLink.href = pngUrl;
                                            downloadLink.download = 'shooting-zusage-qrcode.png';
                                            downloadLink.click();
                                            URL.revokeObjectURL(url);
                                            toast.success('QR-Code heruntergeladen!');
                                        };
                                        img.src = url;
                                    }
                                }}
                                data-testid="download-qr-code"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Herunterladen
                            </Button>
                            <Button
                                className="flex-1"
                                onClick={() => setShowQrModal(false)}
                            >
                                Schließen
                            </Button>
                        </div>
                    </div>
                    {/* Hidden QR for download */}
                    <div className="hidden">
                        <QRCodeSVG 
                            value={`${window.location.origin}/shooting-zusage/`}
                            size={200}
                            level="H"
                            includeMargin={true}
                            className="qr-download-target"
                        />
                    </div>
                </DialogContent>
            </Dialog>
            </div>
        </div>
    );
};

export default SocialFacesAdminPage;
